import { create } from 'zustand';
import type { CognitoUser } from 'amazon-cognito-identity-js';
import type { PlanLevel, SubscriptionTier, User, UserRole } from '@/types';
import * as cognito from '@/lib/auth/cognito-client';
import { api, ApiClientError } from '@/lib/api/client';

interface MeResponse {
  user: {
    user_id: string;
    email: string;
    name?: string;
    role: UserRole;
    org_id: string | null;
    mfa_enabled?: boolean;
    last_login?: string;
    created_at?: string;
  };
  tier: SubscriptionTier | null;
  plan_level: PlanLevel | null;
  organization: { id: string; name: string | null; slug: string | null } | null;
}

interface AuthStore {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isHydrating: boolean;
  mfaPending: boolean;
  /**
   * True when Cognito has returned NEW_PASSWORD_REQUIRED for the current
   * signin attempt. The SignInPage swaps its form to the new-password
   * fields when this flips on.
   */
  newPasswordRequired: boolean;
  error: string | null;
  /** Internal: Cognito user object held during an MFA challenge. */
  mfaChallengeUser: CognitoUser | null;
  mfaChallengeType: 'MFA_REQUIRED' | 'TOTP_REQUIRED' | null;
  /**
   * Internal: Cognito user object held while waiting for the user to
   * complete the new-password challenge. Different shape from MFA — no
   * code, just a new password.
   */
  newPasswordChallengeUser: CognitoUser | null;

  login: (email: string, password: string) => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => void;
  hydrate: () => Promise<void>;
  clearError: () => void;
}

/**
 * Map Cognito ID-token claims into the existing User shape so the rest of
 * the app keeps working untouched. Used as the initial value until /me
 * enriches with persisted DynamoDB fields (real name, tier, plan_level).
 */
function claimsToUser(claims: cognito.CognitoClaims): User {
  return {
    id: claims.sub,
    email: claims.email,
    name: claims.email.split('@')[0],
    role: claims.role,
    organizationId: claims.organizationId || null,
    mfaEnabled: false,
    lastLogin: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  };
}

/**
 * Hydrate the stored User from a successful GET /me response.
 */
function meResponseToUser(me: MeResponse): User {
  return {
    id: me.user.user_id,
    email: me.user.email,
    name: me.user.name ?? me.user.email.split('@')[0],
    role: me.user.role,
    organizationId: me.user.org_id,
    organizationName: me.organization?.name ?? null,
    organizationSlug: me.organization?.slug ?? null,
    tier: me.tier,
    planLevel: me.plan_level,
    mfaEnabled: me.user.mfa_enabled ?? false,
    lastLogin: me.user.last_login ?? new Date().toISOString(),
    createdAt: me.user.created_at ?? new Date().toISOString(),
  };
}

/**
 * Best-effort enrich the current user from the backend. Failures are logged
 * but never block sign-in (e.g. dev environments without an API yet).
 */
async function fetchMe(): Promise<MeResponse | null> {
  try {
    return await api<MeResponse>('/me');
  } catch (err) {
    if (err instanceof ApiClientError) {
      console.warn('[authStore] /me failed', { code: err.code, status: err.status });
    }
    return null;
  }
}

function friendlyError(err: unknown): string {
  if (err instanceof Error) {
    // Common Cognito error names → user-readable messages
    const name = (err as Error & { name?: string }).name ?? '';
    if (name === 'NotAuthorizedException') return 'Incorrect email or password.';
    if (name === 'UserNotConfirmedException')
      return 'Your account has not been confirmed yet. Check your email.';
    if (name === 'PasswordResetRequiredException')
      return 'Your password must be reset. Please use "Forgot password".';
    if (name === 'UserNotFoundException') return 'No account found for that email.';
    if (name === 'CodeMismatchException') return 'The verification code is incorrect.';
    if (name === 'ExpiredCodeException') return 'The verification code has expired.';
    return err.message;
  }
  return 'Sign-in failed. Please try again.';
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isHydrating: true,
  mfaPending: false,
  newPasswordRequired: false,
  error: null,
  mfaChallengeUser: null,
  mfaChallengeType: null,
  newPasswordChallengeUser: null,

  hydrate: async () => {
    set({ isHydrating: true });
    try {
      const restored = await cognito.restoreSession();
      if (!restored) {
        set({ isHydrating: false });
        return;
      }
      // Immediately set a placeholder user from claims so guarded pages render
      const placeholder = claimsToUser(restored.claims);
      set({ user: placeholder, isAuthenticated: true, isHydrating: false });
      // Enrich from /me in the background; if it fails we keep the placeholder.
      const me = await fetchMe();
      if (me) set({ user: meResponseToUser(me) });
    } catch {
      set({ isHydrating: false });
    }
  },

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      const result = await cognito.signIn(email, password);
      if (result.kind === 'success') {
        set({
          user: claimsToUser(result.result.claims),
          isAuthenticated: true,
          isLoading: false,
          mfaPending: false,
          mfaChallengeUser: null,
          mfaChallengeType: null,
        });
        // Enrich from /me in the background.
        void fetchMe().then((me) => {
          if (me) set({ user: meResponseToUser(me) });
        });
        return;
      }
      if (
        result.challenge.type === 'MFA_REQUIRED' ||
        result.challenge.type === 'TOTP_REQUIRED'
      ) {
        set({
          isLoading: false,
          mfaPending: true,
          mfaChallengeUser: result.challenge.cognitoUser,
          mfaChallengeType: result.challenge.type,
        });
        return;
      }
      if (result.challenge.type === 'NEW_PASSWORD_REQUIRED') {
        // First-login flow: Cognito accepted the temp password and now
        // requires the user to choose their permanent one. SignInPage
        // swaps to the new-password form when newPasswordRequired is true.
        set({
          isLoading: false,
          newPasswordRequired: true,
          newPasswordChallengeUser: result.challenge.cognitoUser,
        });
        return;
      }
      set({
        isLoading: false,
        error: 'Unsupported authentication challenge. Please contact support.',
      });
    } catch (err) {
      set({ isLoading: false, error: friendlyError(err) });
    }
  },

  verifyMfa: async (code: string) => {
    const { mfaChallengeUser, mfaChallengeType } = get();
    if (!mfaChallengeUser || !mfaChallengeType) {
      set({ error: 'MFA challenge expired. Please sign in again.', mfaPending: false });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const result = await cognito.submitMfaCode(
        mfaChallengeUser,
        code,
        mfaChallengeType
      );
      set({
        user: claimsToUser(result.claims),
        isAuthenticated: true,
        isLoading: false,
        mfaPending: false,
        mfaChallengeUser: null,
        mfaChallengeType: null,
      });
      void fetchMe().then((me) => {
        if (me) set({ user: meResponseToUser(me) });
      });
    } catch (err) {
      set({ isLoading: false, error: friendlyError(err) });
    }
  },

  completeNewPassword: async (newPassword: string) => {
    const { newPasswordChallengeUser } = get();
    if (!newPasswordChallengeUser) {
      set({
        error: 'Password reset session expired. Please sign in again.',
        newPasswordRequired: false,
      });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      const result = await cognito.completeNewPasswordChallenge(
        newPasswordChallengeUser,
        newPassword
      );
      set({
        user: claimsToUser(result.claims),
        isAuthenticated: true,
        isLoading: false,
        newPasswordRequired: false,
        newPasswordChallengeUser: null,
      });
      void fetchMe().then((me) => {
        if (me) set({ user: meResponseToUser(me) });
      });
    } catch (err) {
      // Most common failure here: InvalidPasswordException (doesn't meet
      // the pool's policy: min 8, mixed case, digit, symbol). friendlyError
      // surfaces Cognito's message which is already actionable.
      set({ isLoading: false, error: friendlyError(err) });
    }
  },

  logout: () => {
    // Fire-and-forget revocation; local state clears below regardless of
    // whether the Cognito network call succeeds. The user is signed out as
    // far as the SPA is concerned the moment we set isAuthenticated=false.
    void cognito.signOut();
    set({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      mfaPending: false,
      newPasswordRequired: false,
      error: null,
      mfaChallengeUser: null,
      mfaChallengeType: null,
      newPasswordChallengeUser: null,
    });
  },

  clearError: () => {
    set({ error: null });
  },
}));

// Boot-time hydration. Runs once when the module is first imported.
useAuthStore.getState().hydrate();
